/**
 * The home's About band (layout 1 Tighter, Gabriel 2026-10-20): the studio's
 * story on the left and its guide as a card on the right, in one section. It
 * draws the story and guide pieces' behavior halves, so custom sites keep the
 * two pieces and their own looks. Only what the studio filled in renders: no
 * guide and the story takes the full width, no story and the guide card does,
 * neither and the band is gone.
 */
import { partsOf, pieceRoot } from '../../contract/runtime';
import { contract as guideContract } from '../../pieces/guide/contract';
import { useGuide } from '../../pieces/guide/useGuide';
import { contract as storyContract } from '../../pieces/story/contract';
import { useStory } from '../../pieces/story/useStory';
import shared from '../../styles/shared.module.css';
import css from './AboutBand.module.css';

const storyPart = partsOf(storyContract);
const guidePart = partsOf(guideContract);

export function AboutBand() {
  const story = useStory();
  const guide = useGuide();
  const hasStory = story.state.paragraphs.length > 0;
  const hasGuide = Boolean(guide.state.name);
  if (!hasStory && !hasGuide) return null;

  const photo = guide.options.showPhoto ? guide.state.photoUrl : null;
  const role = [guide.state.role, guide.state.credentials].filter(Boolean).join(' · ');
  const label = story.options.heading ?? guide.state.heading ?? 'About the studio';

  return (
    <section className={shared.sec} id="about" aria-label={label}>
      <div className={`${shared.wrap} ${hasStory && hasGuide ? css.grid : ''}`}>
        {hasStory && (
          <div {...pieceRoot(storyContract)} {...storyPart('section')}>
            {story.options.eyebrow && (
              <div className={shared.eyebrow} {...storyPart('eyebrow')}>
                {story.options.eyebrow}
              </div>
            )}
            {story.options.heading && (
              <h2 className={`${shared.h2} ${css.heading}`} {...storyPart('heading')}>
                {story.options.heading}
              </h2>
            )}
            <div className={css.prose} {...storyPart('text')}>
              {story.state.paragraphs.map((p, i) => (
                <p key={i} {...storyPart('paragraph')}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        )}
        {hasGuide && (
          <div className={`${css.guide} ${hasStory ? css.besideStory : ''} ${photo ? '' : css.noPhoto}`} {...pieceRoot(guideContract)} {...guidePart('section')}>
            {photo && (
              <div className={css.photo} {...guidePart('photo')}>
                <img src={photo} alt={guide.state.name ?? ''} loading="lazy" />
              </div>
            )}
            <div>
              {guide.state.heading && (
                <div className={shared.eyebrow} {...guidePart('heading')}>
                  {guide.state.heading}
                </div>
              )}
              <h3 className={`${shared.h3} ${css.name}`} {...guidePart('name')}>
                {guide.state.name}
              </h3>
              {role && (
                <p className={css.role} {...guidePart('role')}>
                  {role}
                </p>
              )}
            </div>
            {guide.state.bio.length > 0 && (
              <div className={css.bio} {...guidePart('bio')}>
                {guide.state.bio.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
